import React, { useEffect, useRef, useState } from "react";
import { isValid, parse } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { useTranslation } from "react-i18next";
import ReactDatePicker, { registerLocale } from "react-datepicker";
import { DatePickerProps as ReactDatePickerProps } from "react-datepicker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays } from "@fortawesome/free-solid-svg-icons";
import Button from "../Button/Button";
import { getFormErrorByName, useFormErrorContext } from "../../../utils/context/FormErrorContext";
import { useReadOnly } from "../../../utils/ReadOnlyContext";
import 'react-datepicker/dist/react-datepicker.css';
import "./DatePicker.css"
registerLocale('en', enUS)
registerLocale('fr', fr)

export interface DatePickerProps extends Omit<ReactDatePickerProps, "onChange" | "date" | "value" | "selected"> {
    id: string,
    name: string,
    date?: Date | string | null,
    onChange?: (date: Date | null, name: string) => void,
    ariaLabel?: string,
    position?:
    | "top"
    | "bottom"
    | "right"
    | "left"
    | "top-start"
    | "top-end"
    | "bottom-start"
    | "bottom-end"
    | "right-start"
    | "right-end"
    | "left-start"
    | "left-end",
    minDate?: Date,
    maxDate?: Date,
    errorMessage?: string | string[],
    disabled?: boolean,
    hasError?: boolean,
}

const DATE_FORMAT = "yyyy-MM-dd"

export default function DatePicker(props: DatePickerProps): JSX.Element {
    const { isReadOnly } = useReadOnly()
    const isParsedDateValid = useRef(true)
    const { i18n } = useTranslation();
    const { t } = useTranslation('common');

    const { date: propsDate, name, ...rest } = props;
    const { removeError, errors } = useFormErrorContext()

    const [currentDateLevel, setCurrentDateLevel] = useState<"year" | "month" | "date">("year");
    const [date, setDate] = useState<Date | null>(null)
    const [error, setError] = useState<string>("")

    const changeLevel = (drillingDown = true) => {
        if (currentDateLevel === "year") {
            if (drillingDown) {
                setCurrentDateLevel("month");
            }
        } else if (currentDateLevel === "month") {
            if (drillingDown) {
                setCurrentDateLevel("date");
            } else {
                setCurrentDateLevel("year");
            }
        } else {
            if (!drillingDown) {
                setCurrentDateLevel("month");
            }
        }
    }

    const parseStringValue = (dateString: string) => {
        if (dateString === "") {
            return { parsedDate: null, isValid: true }
        }
        const parsedDate = parse(dateString, DATE_FORMAT, new Date());
        if (isValid(parsedDate)) {
            return { parsedDate: parsedDate, isValid: true }
        } else {
            return { parsedDate: null, isValid: false }
        }
    }

    const handleDateChangeRaw = (e?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        if (e) {
            isParsedDateValid.current = true
            setError("")
            const currentTarget = e.currentTarget
            if (currentTarget instanceof HTMLInputElement) {
                const dateString = currentTarget.value.trim()
                const { parsedDate, isValid } = parseStringValue(dateString)
                isParsedDateValid.current = isValid
                if (isValid) {
                    setDate(parsedDate);
                } else {
                    setError(t("datepicker-invalid-date"))
                }
            } else if (currentTarget.tagName === "DIV"
                && currentTarget.className.includes("react-datepicker")) {
                changeLevel()
            }
        }
    }

    const dateSelected = (date: Date | null) => {
        removeError(getFormErrorByName(errors, props.name as string)?.primaryKey as string)
        if (isParsedDateValid.current) {
            if (date === null) {
                setCurrentDateLevel("year")
            } else {
                changeLevel()
            }
            setDate(date);
            if (props.onChange) {
                props.onChange(date, name);
            }
        }
    }

    const handleBlur = (e: React.FocusEvent<HTMLElement, Element>) => {
        const target = e.target as HTMLInputElement
        const dateString = target.value.trim()
        const { parsedDate, isValid } = parseStringValue(dateString)
        if (isValid) {
            dateSelected(parsedDate)
        } else {
            dateSelected(date)
        }
        isParsedDateValid.current = true
        setError("")
    }

    useEffect(() => {
        isParsedDateValid.current = true
        setError("")
        if (props.date === null || props.date === undefined) {
            setDate(null)
            return
        }
        if (typeof props.date === 'string') {
            const dateString = props.date.trim()
            const { parsedDate } = parseStringValue(dateString)
            setDate(parsedDate)
        } else {
            setDate(props.date)
        }
    }, [props.date])

    return (
        <div className="datepicker-wrapper">
            <div id={props.id + "-container"} className="datepicker-container"
                data-primary-key={getFormErrorByName(errors, props.name)?.primaryKey as string}>
                <div className="datepicker-input-container">
                    <ReactDatePicker {...rest} name={name} id={props.id + "-input"} dateFormat={DATE_FORMAT}
                        className={`${props.className ? props.className : ""} datepicker-input ${(props.hasError && 'table-error')}`}
                        placeholderText={props.placeholderText ? props.placeholderText : t("datepicker-placeholder")}
                        title={props.title} autoComplete="off"
                        aria-label={props.ariaLabel ?? props.title ?? name}
                        locale={i18n.language} closeOnScroll={false} showPopperArrow={false}
                        popperPlacement={props.position ?? "right-start"}
                        disabled={props.disabled === undefined ? isReadOnly : props.disabled}
                        selected={date}
                        selectsRange={undefined}
                        selectsMultiple={undefined}
                        onChange={(date: Date | null) => { dateSelected(date) }}
                        onBlur={(e) => handleBlur(e)}
                        onChangeRaw={handleDateChangeRaw}
                        onCalendarOpen={() => {
                            if (props.onCalendarOpen) {
                                props.onCalendarOpen()
                            }
                            setCurrentDateLevel("year")
                        }}
                        onCalendarClose={props.onCalendarClose}
                        shouldCloseOnSelect={currentDateLevel === "date"}
                        showYearPicker={currentDateLevel === "year"}
                        showMonthYearPicker={currentDateLevel === "month"}
                        showMonthYearDropdown={undefined}
                    >
                        {currentDateLevel !== "year" &&
                            <Button text={t("back")}
                                className="datepicker-back-button"
                                onClick={(e) => { e.preventDefault(); changeLevel(false); }} />
                        }
                    </ReactDatePicker>
                </div>
                <div className="datepicker-icon-container">
                    <FontAwesomeIcon icon={faCalendarDays} className="datepicker-icon"
                        onClick={() => document.getElementById(props.id + "-input")?.focus()} />
                </div>
            </div>
            {(error || props.errorMessage || props.hasError) && (
                <div id={props.id + "-error"} className="input-error-message">
                    {
                        props.errorMessage ?
                            props.errorMessage
                            :
                            error
                    }
                </div>
            )}
        </div>
    )
}